<template>
  <div class="impersonation-banner">
    <div class="impersonation-banner__body">
      <span class="impersonation-banner__text">
        CURRENTLY LOGGED IN AS: {{ fullName }}<template v-if="companyName"> ({{ companyName }})</template>
      </span>
      <span class="impersonation-banner__note">
        Questions you ask in Ask now aren't saved to {{ historyOwner }} history.
      </span>
    </div>
    <button class="impersonation-banner__exit" :disabled="exiting" @click="exit">
      {{ exiting ? 'Exiting...' : 'Exit' }}
    </button>
  </div>
</template>

<script>
import axios from 'axios';
import { useUserStore } from '@/stores/userStore';
import { mapState } from 'pinia';

export default {
  name: 'ImpersonationBanner',
  data() {
    return {
      exiting: false,
    };
  },
  computed: {
    ...mapState(useUserStore, ['impersonatedUser']),
    fullName() {
      const u = this.impersonatedUser;
      if (!u) return '';
      return [u.firstName, u.lastName].filter(Boolean).join(' ');
    },
    // KAN-79: impersonated asks are answered but never saved to the user's History.
    historyOwner() {
      const firstName = String(this.impersonatedUser?.firstName || '').trim();
      return firstName ? `${firstName}'s` : "this user's";
    },
    companyName() {
      return this.impersonatedUser?.companyName || '';
    },
  },
  methods: {
    async exit() {
      this.exiting = true;
      const token = this.$cookies.get('teamwrkr-token');
      try {
        await axios.post(
          `${process.env.VUE_APP_API_URL}/superuser/exit`,
          {},
          { headers: { Authorization: token } }
        );
      } catch {
        // best-effort log — always proceed with exit
      }
      const userStore = useUserStore();
      let restored = false;
      try {
        restored = await userStore.stopImpersonation();
      } catch {
        // restoring the super user's session failed — fall through to signing out
        restored = false;
      }
      if (restored) {
        this.$router.push('/superuser');
        return;
      }
      // spec-ask-ux-impersonation-and-composer: no super-user session to return to (no stored
      // token, the cookie is no longer an impersonation, or restoring failed) — sign out instead.
      userStore.logout();
      this.$buefy?.toast?.open({ message: 'Please sign in again.', type: 'is-info' });
      this.$router.push('/welcome');
    },
  },
};
</script>

<style lang="scss" scoped>
.impersonation-banner {
  width: 100%;
  background: $color-primary;
  color: #fff;
  font-weight: 700;
  text-align: center;
  padding: 0.6rem 1.5rem;
  letter-spacing: 0.04em;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  position: sticky;
  top: 0;
  z-index: 50;

  &__body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
  }

  &__note {
    font-weight: 400;
    font-size: 0.8rem;
    letter-spacing: normal;
    opacity: 0.9;
  }

  &__exit {
    background: rgba(255, 255, 255, 0.18);
    border: 1.5px solid rgba(255, 255, 255, 0.6);
    color: #fff;
    border-radius: 0.4rem;
    padding: 0.25rem 0.9rem;
    font-weight: 700;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background 0.15s;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.3);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
